import { ReactElement, useMemo } from 'react';

import { addressFormatter, BindingCreator, createTranslatedText, useTranslate } from '@dreryk/react-common';
import { FilterPanel, Multiselect, SecondaryText, StyledFlex } from '@dreryk/react-components';

import { HealthcareProviderLocation } from '~libs.shared.healthcare-provider-location';

import {
   WaitlistFilterPanelFormBean,
   WaitlistFilterPanelSearchParam,
   WaitlistFilterPanelStorageKey,
} from '../../WaitlistFilterPanel.types';
import { translationKeys } from '../../../translations';

import {
   createHealthcareProviderLocationsCodec,
   createHealthcareProviderLocationsTriggerLabelExtractor,
   healthcareProviderLocationsDeselectorsPropsMapper,
} from './HealthcareProviderLocationController.utils';
import { StyledHealthcareProviderLocationMultiselect } from './HealthcareProviderLocationMultiselect.styled';

type HealthcareProviderLocationControllerProps = {
   registerController: BindingCreator<WaitlistFilterPanelFormBean, 'healthcareProviderLocations'>;
   locations: HealthcareProviderLocation[];
};

export const HealthcareProviderLocationController = (
   props: HealthcareProviderLocationControllerProps
): ReactElement => {
   const translate = useTranslate();
   const codec = useMemo(() => createHealthcareProviderLocationsCodec(props.locations), [props.locations]);
   return (
      <FilterPanel.Controller<WaitlistFilterPanelFormBean, 'healthcareProviderLocations'>
         name={'healthcareProviderLocations'}
         searchParam={WaitlistFilterPanelSearchParam.HEALTHCARE_PROVIDER_LOCATION}
         storageKey={WaitlistFilterPanelStorageKey.HEALTHCARE_PROVIDER_LOCATION}
         codec={codec}
         chipsLabel={translationKeys.healthcareProviderLocation}
         getDeselectorsProps={healthcareProviderLocationsDeselectorsPropsMapper}
      >
         {({ applyFilter }) => (
            <StyledHealthcareProviderLocationMultiselect<HealthcareProviderLocation>
               testId={'healthcareProviderLocations'}
               triggerLabelExtractor={createHealthcareProviderLocationsTriggerLabelExtractor(
                  props.locations.length,
                  translate
               )}
               controller={props.registerController('healthcareProviderLocations')}
               onHide={applyFilter}
               label={translationKeys.healthcareProviderLocation}
               placeholder={translationKeys.chooseHealthcareProviderLocation}
               itemKeyGenerator={(location) => location.id.asString()}
               compact
            >
               <>
                  <Multiselect.SelectAll testId={'selectAll'} items={props.locations} />
                  <Multiselect.List<HealthcareProviderLocation> items={props.locations} keyExtractor={'id'}>
                     {(item) => (
                        <Multiselect.Item
                           item={item}
                           label={
                              <StyledFlex direction={'column'}>
                                 <span>{item.name}</span>
                                 <SecondaryText
                                    testId={'healthcareProviderLocationAddress'}
                                    text={createTranslatedText(addressFormatter(item.address))}
                                 />
                              </StyledFlex>
                           }
                           testId={item.id.asString()}
                        />
                     )}
                  </Multiselect.List>
               </>
            </StyledHealthcareProviderLocationMultiselect>
         )}
      </FilterPanel.Controller>
   );
};
